import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

/**
 * @graph-node {Page}
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business 設定ページ。テーマ切替・言語切替・アカウント情報表示を提供
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects useTheme [calls] テーマ管理フック呼び出し
 */
export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

/** @graph-connects none */
function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  const themes = [
    { value: 'dark' as const, label: t('settings.dark') },
    { value: 'light' as const, label: t('settings.light') },
    { value: 'system' as const, label: t('settings.system') },
  ];

  const languages = [
    { value: 'ja', label: '日本語' },
    { value: 'en', label: 'English' },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('settings.title')}
        </h1>
      </div>

      {/* テーマ設定 */}
      <Card variant="glass">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
          {t('settings.themeTitle')}
        </h2>
        <div className="flex gap-2">
          {themes.map((t) => (
            <Button
              key={t.value}
              variant={theme === t.value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                setTheme(t.value);
              }}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </Card>

      {/* 言語設定 */}
      <Card variant="glass">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
          {t('settings.languageTitle')}
        </h2>
        <div className="flex gap-2">
          {languages.map((lang) => (
            <Button
              key={lang.value}
              variant={i18n.language.startsWith(lang.value) ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => void i18n.changeLanguage(lang.value)}
            >
              {lang.label}
            </Button>
          ))}
        </div>
      </Card>

      {/* アカウント情報 */}
      {user && (
        <Card variant="glass">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
            {t('settings.accountTitle')}
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--color-text-muted)]">{t('settings.name')}</span>
              <span className="text-[var(--color-text-primary)]">{user.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[var(--color-text-muted)]">{t('settings.email')}</span>
              <span className="text-[var(--color-text-primary)]">{user.email}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
